import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompanyInfoRestDataProvider } from '../../../data/CompanyInfoRestDataProvider';
import { BinaryFileRestDataProvider } from '../../../data/BinaryFileRestDataProvider';
import { TimeWritingRestDataProvider } from '../../../data/TimeWritingRestDataProvider';
import { UsersRestDataProvider, User } from '../../../data/UsersRestDataProvider';
import { CompanyInfo } from '../../../models/CompanyInfo';
import { Customer } from '../../../models/Customer';
import { CustomerInvoice } from '../../../models/CustomerInvoice';
import { TimeWritingEntry } from '../../../models/TimeWritingEntry';
import PrintableView, { PrintablePage } from '../../common/PrintableView/PrintableView';
import { usePrintablePages } from '../../common/PrintableView/usePrintablePages';
import './InvoiceView.scss';

interface InvoiceViewProps {
    open: boolean;
    invoice: CustomerInvoice;
    customer: Customer | undefined;
    onClose: () => void;
}

const fmtDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '—';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    return `${parseInt(parts[2])}-${parseInt(parts[1])}-${parts[0]}`;
};

const fmtMoney = (amount: number): string =>
    `€\u00a0${amount.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtHours = (h: number): string =>
    h.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const extractBankName = (iban: string): string => {
    const clean = iban.replace(/\s/g, '').toUpperCase();
    if (clean.startsWith('NL') && clean.length >= 8) {
        const code = clean.substring(4, 8);
        const names: Record<string, string> = {
            KNAB: 'Knab', ABNA: 'ABN AMRO', INGB: 'ING', RABO: 'Rabobank',
            TRIO: 'Triodos', BUNQ: 'bunq', SNSB: 'SNS', ASNB: 'ASN Bank',
        };
        return names[code] ?? code;
    }
    return '';
};

const hasExemptLines = (invoice: CustomerInvoice): boolean =>
    invoice.lineItems.some(i => i.vatRate?.isMoved || i.vatRate?.isExempt);

type TwRow =
    | { kind: 'user-header'; label: string }
    | { kind: 'entry'; date: string; description: string; hours: number }
    | { kind: 'user-subtotal'; label: string; hours: number };

const InvoiceView: React.FC<InvoiceViewProps> = ({ open, invoice, customer, onClose }) => {
    const { t } = useTranslation(['common', 'screens']);
    const provider = useMemo(() => new CompanyInfoRestDataProvider(), []);
    const fileProvider = useMemo(() => new BinaryFileRestDataProvider(), []);
    const twProvider = useMemo(() => new TimeWritingRestDataProvider(), []);
    const usersProvider = useMemo(() => new UsersRestDataProvider(), []);
    const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [timeWritingEntries, setTimeWritingEntries] = useState<TimeWritingEntry[]>([]);
    const [twUsers, setTwUsers] = useState<User[]>([]);

    useEffect(() => {
        if (!open) return;
        provider.getInfo().then(setCompanyInfo).catch(() => { });
    }, [open, provider]);

    useEffect(() => {
        if (!companyInfo?.logoFileId) { setLogoUrl(null); return; }
        let objectUrl: string | null = null;
        fileProvider.download(companyInfo.logoFileId).then(blob => {
            objectUrl = URL.createObjectURL(blob);
            setLogoUrl(objectUrl);
        }).catch(() => setLogoUrl(null));
        return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [companyInfo?.logoFileId, fileProvider]);

    useEffect(() => {
        if (!open || !invoice.includeTimeWritingOverview || !invoice.projectId) {
            setTimeWritingEntries([]);
            setTwUsers([]);
            return;
        }
        Promise.all([
            twProvider.getByProject(invoice.projectId),
            usersProvider.getAll(),
        ]).then(([entries, users]) => {
            setTimeWritingEntries(entries);
            setTwUsers(users);
        }).catch(() => { });
    }, [open, invoice.includeTimeWritingOverview, invoice.projectId, twProvider, usersProvider]);

    const twUserMap = useMemo(
        () => Object.fromEntries(twUsers.map(u => [u.id ?? '', u.fullName])),
        [twUsers]
    );

    const allTwRows = useMemo((): TwRow[] => {
        if (!invoice.includeTimeWritingOverview || !timeWritingEntries.length) return [];
        const filtered = timeWritingEntries.filter(e => {
            if (invoice.timeWritingFrom && e.date < invoice.timeWritingFrom) return false;
            if (invoice.timeWritingTo && e.date > invoice.timeWritingTo) return false;
            return true;
        });
        if (!filtered.length) return [];

        const sorted = [...filtered].sort((a, b) => a.date.localeCompare(b.date));
        const groups = new Map<string, TimeWritingEntry[]>();
        for (const entry of sorted) {
            const arr = groups.get(entry.userId) ?? [];
            arr.push(entry);
            groups.set(entry.userId, arr);
        }

        const rows: TwRow[] = [];
        const sortedGroups = [...groups.entries()].sort(([aId], [bId]) =>
            (twUserMap[aId] ?? aId).localeCompare(twUserMap[bId] ?? bId)
        );
        for (const [userId, entries] of sortedGroups) {
            const label = twUserMap[userId] ?? userId;
            rows.push({ kind: 'user-header', label });
            let subtotal = 0;
            for (const e of entries) {
                rows.push({ kind: 'entry', date: e.date, description: e.activityDescription, hours: Number(e.hours) });
                subtotal += Number(e.hours);
            }
            rows.push({ kind: 'user-subtotal', label, hours: subtotal });
        }

        return rows;
    }, [invoice.includeTimeWritingOverview, invoice.timeWritingFrom, invoice.timeWritingTo, timeWritingEntries, twUserMap]);

    // Available height for TW rows per page (CSS px at 96px/inch = 3.7795px/mm):
    // 297mm paper − 16mm top − 14mm bottom − ~27mm company header − ~15mm tw-header
    // − ~9mm table thead − ~11mm grand-total reserved on every page
    const TW_AVAILABLE_PX = (297 - 16 - 14 - 27 - 15 - 9 - 11) * (96 / 25.4);

    const { pages: timeWritingPages, measurePortal } = usePrintablePages<TwRow>({
        items: allTwRows,
        availableHeightPx: TW_AVAILABLE_PX,
        tableClassName: 'inv-view__tw-table',
        renderMeasureRow: (row, i) => {
            if (row.kind === 'user-header')
                return <tr key={i} className="inv-view__tw-user-row"><td colSpan={3}>{row.label}</td></tr>;
            if (row.kind === 'user-subtotal')
                return <tr key={i} className="inv-view__tw-subtotal-row"><td colSpan={2}>Totaal {row.label}</td><td className="inv-view__tw-td--num">{fmtHours(row.hours)}</td></tr>;
            return <tr key={i} className="inv-view__tw-entry-row"><td className="inv-view__tw-td--date">{fmtDate(row.date)}</td><td>{row.description}</td><td className="inv-view__tw-td--num">{fmtHours(row.hours)}</td></tr>;
        },
    });

    const totalTwHours = useMemo(
        () => timeWritingEntries
            .filter(e => {
                if (invoice.timeWritingFrom && e.date < invoice.timeWritingFrom) return false;
                if (invoice.timeWritingTo && e.date > invoice.timeWritingTo) return false;
                return true;
            })
            .reduce((s, e) => s + Number(e.hours), 0),
        [timeWritingEntries, invoice.timeWritingFrom, invoice.timeWritingTo]
    );

    if (!open) return null;

    const totalExVat = invoice.lineItems.reduce((s, i) => s + i.totalPriceExVat, 0);
    const totalIncVat = invoice.lineItems.reduce((s, i) => s + i.totalPriceIncVat, 0);
    const iban = companyInfo?.bankAccountNumber ?? '';
    const bankName = extractBankName(iban);

    // ── Shared company header (reused on invoice page and every TW page) ──
    const companyHeader = (
        <div className="inv-view__company-header">
            {logoUrl ? (
                <img src={logoUrl} alt="Company Logo" className="inv-view__logo-img" />
            ) : (
                <div className="inv-view__logo-placeholder">Logo</div>
            )}
            <div className="inv-view__company-name">{companyInfo?.companyName ?? ''}</div>
        </div>
    );

    // ── Page 0: invoice content ──
    const invoicePageContent = (
        <>
            {companyHeader}

            {/* ── Two-column meta block ── */}
            <div className="inv-view__meta-row">

                {/* Left: customer address */}
                <div className="inv-view__customer-address">
                    {customer?.name && <div>{customer.name}</div>}
                    {customer?.useInvoiceAddress ? (
                        <>
                            {customer?.invoiceAddressLine1 && <div>{customer.invoiceAddressLine1}</div>}
                            {customer?.invoiceAddressLine2 && <div>{customer.invoiceAddressLine2}</div>}
                            {(customer?.invoiceZipCode || customer?.invoiceCity) && (
                                <div>{[customer?.invoiceZipCode, customer?.invoiceCity].filter(Boolean).join(' ')}</div>
                            )}
                            {customer?.invoiceCountry && <div>{customer.invoiceCountry}</div>}
                        </>
                    ) : (
                        <>
                            {customer?.addressLine1 && <div>{customer.addressLine1}</div>}
                            {customer?.addressLine2 && <div>{customer.addressLine2}</div>}
                            {(customer?.zipCode || customer?.city) && (
                                <div>{[customer?.zipCode, customer?.city].filter(Boolean).join(' ')}</div>
                            )}
                            {customer?.country && <div>{customer.country}</div>}
                        </>
                    )}
                </div>

                {/* Right: invoice details */}
                <div className="inv-view__details">
                    <div className="inv-view__details-title">Factuur</div>
                    <table className="inv-view__details-table">
                        <tbody>
                            {invoice.invoiceNumber && (
                                <tr>
                                    <td className="inv-view__dt-label">Factuurnummer</td>
                                    <td className="inv-view__dt-value">{invoice.invoiceNumber}</td>
                                </tr>
                            )}
                            {customer?.customerNumber && (
                                <tr>
                                    <td className="inv-view__dt-label">Debiteurennummer</td>
                                    <td className="inv-view__dt-value">{customer.customerNumber}</td>
                                </tr>
                            )}
                            {invoice.invoiceDate && (
                                <tr>
                                    <td className="inv-view__dt-label">Factuurdatum</td>
                                    <td className="inv-view__dt-value">{fmtDate(invoice.invoiceDate)}</td>
                                </tr>
                            )}
                            {invoice.deliveryDate && (
                                <tr>
                                    <td className="inv-view__dt-label">Leverdatum</td>
                                    <td className="inv-view__dt-value">{fmtDate(invoice.deliveryDate)}</td>
                                </tr>
                            )}
                            {invoice.reference && (
                                <tr>
                                    <td className="inv-view__dt-label">Onze referentie</td>
                                    <td className="inv-view__dt-value">{invoice.reference}</td>
                                </tr>
                            )}
                            {invoice.customerReference && (
                                <tr>
                                    <td className="inv-view__dt-label">Uw referentie</td>
                                    <td className="inv-view__dt-value">{invoice.customerReference}</td>
                                </tr>
                            )}
                            {customer?.btwId && (
                                <tr>
                                    <td className="inv-view__dt-label">Uw btw-nummer</td>
                                    <td className="inv-view__dt-value">{customer.btwId}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Comments / references block ── */}
            {invoice.comments && (
                <div className="inv-view__comments">
                    {invoice.comments.split('\n').map((line, i) => (
                        <div key={i} className="inv-view__comment-line">{line || '\u00a0'}</div>
                    ))}
                </div>
            )}

            {/* ── Line items table ── */}
            <table className="inv-view__items">
                <thead>
                    <tr>
                        <th className="inv-view__th inv-view__th--desc">Omschrijving</th>
                        <th className="inv-view__th inv-view__th--num">Aantal</th>
                        <th className="inv-view__th inv-view__th--num">Prijs/stuk</th>
                        <th className="inv-view__th inv-view__th--num">Totaal ex BTW</th>
                        <th className="inv-view__th inv-view__th--num">BTW%</th>
                        <th className="inv-view__th inv-view__th--num">Totaal inc BTW</th>
                    </tr>
                </thead>
                <tbody>
                    {invoice.lineItems.map(item => {
                        const effectivePct = (item.vatRate?.isMoved || item.vatRate?.isExempt)
                            ? 0
                            : (item.vatRate?.percentage ?? 0);
                        return (
                            <tr key={item.id} className="inv-view__item-row">
                                <td>{item.description}</td>
                                <td className="inv-view__td--num">{item.quantity}</td>
                                <td className="inv-view__td--num">{fmtMoney(item.unitPriceExVat)}</td>
                                <td className="inv-view__td--num">{fmtMoney(item.totalPriceExVat)}</td>
                                <td className="inv-view__td--num">{effectivePct}%</td>
                                <td className="inv-view__td--num">{fmtMoney(item.totalPriceIncVat)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* ── Totals ── */}
            <div className="inv-view__totals-section">
                <div className="inv-view__totals-note">
                    {hasExemptLines(invoice) && (
                        <em>De bedragen op deze factuur zijn vrijgesteld van BTW.</em>
                    )}
                </div>
                <div className="inv-view__totals-amounts">
                    <div className="inv-view__totals-col">
                        <div className="inv-view__totals-header">Totaal ex BTW</div>
                        <div className="inv-view__totals-value">{fmtMoney(totalExVat)}</div>
                    </div>
                    <div className="inv-view__totalvat-col">
                        <div className="inv-view__totals-header">BTW</div>
                        <div className="inv-view__totals-value">{fmtMoney(totalIncVat - totalExVat)}</div>
                    </div>
                    <div className="inv-view__totals-col">
                        <div className="inv-view__totals-header">Totaal te betalen (EUR)</div>
                        <div className="inv-view__totals-value inv-view__totals-value--grand">{fmtMoney(totalIncVat)}</div>
                    </div>
                </div>
            </div>

            {/* ── Payment details ── */}
            <div className="inv-view__payment-section">
                <div className="inv-view__payment-half">
                    {invoice.paymentDueDate && (
                        <div className="inv-view__payment-row">
                            <span className="inv-view__payment-label">Vervaldatum</span>
                            <span className="inv-view__payment-value">{fmtDate(invoice.paymentDueDate)}</span>
                        </div>
                    )}
                    {invoice.invoiceNumber && (
                        <div className="inv-view__payment-row">
                            <span className="inv-view__payment-label">Factuurnummer</span>
                            <span className="inv-view__payment-value">{invoice.invoiceNumber}</span>
                        </div>
                    )}
                    <div className="inv-view__payment-hint">
                        <em>Vermeld het factuurnummer bij uw betaling</em>
                    </div>
                </div>
                <div className="inv-view__payment-half">
                    {bankName && (
                        <div className="inv-view__payment-row">
                            <span className="inv-view__payment-label">Bank</span>
                            <span className="inv-view__payment-value">{bankName}</span>
                        </div>
                    )}
                    {iban && (
                        <div className="inv-view__payment-row">
                            <span className="inv-view__payment-label">IBAN</span>
                            <span className="inv-view__payment-value">{iban}</span>
                        </div>
                    )}
                    {companyInfo?.companyName && (
                        <div className="inv-view__payment-row">
                            <span className="inv-view__payment-label">Rekeninghouder</span>
                            <span className="inv-view__payment-value">{companyInfo.companyName}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Company footer ── */}
            <div className="inv-view__company-footer">
                <div className="inv-view__footer-col">
                    <div className="inv-view__footer-label">Adres</div>
                    {companyInfo?.companyName && <div>{companyInfo.companyName}</div>}
                    {companyInfo?.addressLine1 && <div>{companyInfo.addressLine1}</div>}
                    {(companyInfo?.zipCode || companyInfo?.city) && (
                        <div>{[companyInfo?.zipCode, companyInfo?.city].filter(Boolean).join(' ')}</div>
                    )}
                </div>
                <div className="inv-view__footer-col">
                    {companyInfo?.phoneNumber && (
                        <>
                            <div className="inv-view__footer-label">Telefoon</div>
                            <div>{companyInfo.phoneNumber}</div>
                        </>
                    )}
                    {companyInfo?.emailAddress && (
                        <>
                            <div className="inv-view__footer-label">Email</div>
                            <div>{companyInfo.emailAddress}</div>
                        </>
                    )}
                </div>
                <div className="inv-view__footer-col">
                    {companyInfo?.kvkNumber && (
                        <>
                            <div className="inv-view__footer-label">KvK Nummer</div>
                            <div>{companyInfo.kvkNumber}</div>
                        </>
                    )}
                </div>
                <div className="inv-view__footer-col">
                    {companyInfo?.btwId && (
                        <>
                            <div className="inv-view__footer-label">BTW Nummer</div>
                            <div>{companyInfo.btwId}</div>
                        </>
                    )}
                </div>
            </div>
        </>
    );

    // ── Pages 1+: time-writing pages ──
    const twPageContents: PrintablePage[] = timeWritingPages.map((pageRows, pageIdx) => ({
        label: 'Urenspecificatie',
        content: (
            <>
                {companyHeader}

                {/* ── Section header ── */}
                <div className="inv-view__tw-header">
                    <div className="inv-view__tw-title">Urenspecificatie</div>
                    <div className="inv-view__tw-meta">
                        {invoice.invoiceNumber && <span>Factuur {invoice.invoiceNumber}</span>}
                        {(invoice.timeWritingFrom || invoice.timeWritingTo) && (
                            <span>
                                {invoice.invoiceNumber ? ' ' : ''}Periode: {fmtDate(invoice.timeWritingFrom)} t/m {fmtDate(invoice.timeWritingTo)}
                            </span>
                        )}
                        {timeWritingPages.length > 1 && (
                            <span> Pagina {pageIdx + 1} van {timeWritingPages.length}</span>
                        )}
                    </div>
                </div>

                {/* ── Entries table ── */}
                <table className="inv-view__tw-table">
                    <thead>
                        <tr>
                            <th className="inv-view__tw-th inv-view__tw-th--date">Datum</th>
                            <th className="inv-view__tw-th">Omschrijving</th>
                            <th className="inv-view__tw-th inv-view__tw-th--num">Uren</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.map((row, rowIdx) => {
                            if (row.kind === 'user-header') {
                                return (
                                    <tr key={rowIdx} className="inv-view__tw-user-row">
                                        <td colSpan={3}>{row.label}</td>
                                    </tr>
                                );
                            }
                            if (row.kind === 'user-subtotal') {
                                return (
                                    <tr key={rowIdx} className="inv-view__tw-subtotal-row">
                                        <td colSpan={2}>Totaal {row.label}</td>
                                        <td className="inv-view__tw-td--num">{fmtHours(row.hours)}</td>
                                    </tr>
                                );
                            }
                            return (
                                <tr key={rowIdx} className="inv-view__tw-entry-row">
                                    <td className="inv-view__tw-td--date">{fmtDate(row.date)}</td>
                                    <td>{row.description}</td>
                                    <td className="inv-view__tw-td--num">{fmtHours(row.hours)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>

                {/* ── Grand total on last TW page only ── */}
                {pageIdx === timeWritingPages.length - 1 && (
                    <div className="inv-view__tw-grand-total">
                        Totaal uren: {fmtHours(totalTwHours)}
                    </div>
                )}
            </>
        ),
    }));

    const pages: PrintablePage[] = [
        { label: 'Factuur', content: invoicePageContent },
        ...twPageContents,
    ];

    return (
        <>
            {measurePortal}

            <PrintableView
                open={open}
                title={t('screens:customerInvoiceDialog.invoicePreview.title')}
                onClose={onClose}
                pages={pages}
                resetKey={invoice.id}
            />
        </>
    );
};

export default InvoiceView;
